import { type PropsWithChildren, useEffect, useRef } from 'react';

/**
 * Mouse-friendly horizontal panning for the Customers table only.
 * The shared Table renders its scroll viewport internally as `.table-scroll`.
 * Event delegation on this outer shell also works while the table is loading.
 */
export function CustomersTableScroll({ children }: PropsWithChildren) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const getViewport = () => root.querySelector<HTMLElement>('.table-scroll');
    const hasOverflow = (viewport: HTMLElement) => viewport.scrollWidth > viewport.clientWidth + 1;

    // Native trackpad horizontal gestures remain native; a standard vertical
    // mouse wheel moves horizontally while this table can still move that way.
    // At the left/right edge we do not preventDefault, so the page can scroll.
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY) * 0.5) {
        return;
      }

      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('select, input, textarea, [role="listbox"], [role="combobox"]')
      ) {
        return;
      }

      const viewport = getViewport();
      if (!viewport || !hasOverflow(viewport) || event.deltaY === 0) return;

      const multiplier =
        event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? viewport.clientWidth : 1;
      const delta = event.deltaY * multiplier;
      const next = Math.max(
        0,
        Math.min(viewport.scrollWidth - viewport.clientWidth, viewport.scrollLeft + delta)
      );

      // Let ordinary vertical page scrolling resume at either horizontal edge.
      if (Math.abs(next - viewport.scrollLeft) < 1) return;

      event.preventDefault();
      viewport.scrollLeft = next;
    };

    type DragState = {
      pointerId: number;
      startX: number;
      startScrollLeft: number;
      viewport: HTMLElement;
      moved: boolean;
    };
    let drag: DragState | null = null;

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Never steal table header sorting/resizing or actionable cell controls.
      if (
        !target.closest('.customers-table tbody td') ||
        target.closest(
          'button, a, input, select, textarea, label, [role="button"], ' +
            '[contenteditable="true"], [data-resize-handle], [class*="resize-handle"]'
        )
      ) {
        return;
      }

      const viewport = getViewport();
      if (!viewport || !hasOverflow(viewport)) return;

      drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: viewport.scrollLeft,
        viewport,
        moved: false,
      };
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const distance = event.clientX - drag.startX;
      if (!drag.moved && Math.abs(distance) < 5) return;
      drag.moved = true;
      root.classList.add('customers-table-scroll-shell--dragging');
      drag.viewport.scrollLeft = drag.startScrollLeft - distance;
      event.preventDefault();
    };

    const stopDragging = (event: PointerEvent) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      drag = null;
      root.classList.remove('customers-table-scroll-shell--dragging');
    };

    const onKeyDown = (event: KeyboardEvent) => {
      // Keep native arrow controls on inputs and interactive elements.
      if (event.target !== root || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) {
        return;
      }
      const viewport = getViewport();
      if (!viewport || !hasOverflow(viewport)) return;
      event.preventDefault();
      viewport.scrollBy({ left: event.key === 'ArrowRight' ? 220 : -220, behavior: 'smooth' });
    };

    root.addEventListener('wheel', onWheel, { passive: false });
    root.addEventListener('pointerdown', onPointerDown);
    root.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', stopDragging);
    window.addEventListener('pointercancel', stopDragging);

    return () => {
      root.removeEventListener('wheel', onWheel);
      root.removeEventListener('pointerdown', onPointerDown);
      root.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stopDragging);
      window.removeEventListener('pointercancel', stopDragging);
      root.classList.remove('customers-table-scroll-shell--dragging');
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="customers-table-scroll-shell"
      tabIndex={0}
      role="region"
      aria-label="Customers table. Use the mouse wheel, drag an empty cell, or press left and right arrows to scroll horizontally."
    >
      {children}
    </div>
  );
}
